// Shared hire-driver rating sheet — used by both the driver list (rate a completed
// booking) and the driver profile (rate after a call). Extracted so the two surfaces
// share one implementation rather than each carrying its own copy.
//
// Props: booking { driverId, driverName }, onClose(), onSuccess(). The four criteria are
// hire-specific and map to the same backend keys the aggregate reads.
import { useEffect, useState } from "react";
import { apiRequest } from "../utils/api";
import { getAuth } from "../utils/auth";
import { useLanguage } from "../context/LanguageContext";

export const HIRE_RATING_CRITERIA = [
  { k: "punctuality", label: "On Time" },
  { k: "driverBehaviour", label: "Behaviour" },
  { k: "drivingQuality", label: "Driving Quality" },
  { k: "safety", label: "Safety" },
];

export default function HireRatingModal({ booking, onClose, onSuccess }) {
  const { t } = useLanguage();
  const auth = getAuth();
  const emptyCriteria = { punctuality: 0, driverBehaviour: 0, drivingQuality: 0, safety: 0 };
  const [criteria, setCriteria] = useState(emptyCriteria);
  const [review, setReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!booking?.driverId) return;
    setLoading(true);
    setCriteria(emptyCriteria);
    setReview("");
    setIsUpdating(false);

    apiRequest(`/ratings/me/${encodeURIComponent(booking.driverId)}`)
      .then(res => {
        if (res?.rating) {
          setIsUpdating(true);
          // Restore whatever criteria were stored, keeping any unset at 0.
          setCriteria({ ...emptyCriteria, ...(res.rating.criteria || {}) });
          setReview(res.rating.review || "");
        } else {
          setIsUpdating(false);
          setCriteria(emptyCriteria);
          setReview("");
        }
      })
      .catch(err => {
        console.error("Failed to fetch existing rating", err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.driverId]);

  const setCriterion = (k, v) => setCriteria(prev => ({ ...prev, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    // At least one criterion must be set — the same "score > 0" rule the backend
    // enforces, applied here so the customer sees it before the round trip.
    if (Object.values(criteria).every(v => v === 0)) {
      return setError("Please rate at least one area");
    }
    setError("");
    setSubmitting(true);
    try {
      await apiRequest("/ratings", {
        method: "POST",
        body: JSON.stringify({
          targetId: booking.driverId,
          targetType: "PERSONAL_DRIVER",
          criteria,
          review,
          raterPhone: auth?.phone,
          raterRole: auth?.role || "USER",
          raterName: auth?.name || auth?.displayName || "Customer",
        }),
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || "Call the driver first, then you can rate them.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-[#1A1A2E]">{t("rateDriver").replace("Driver", booking.driverName || "Driver")}</p>
            <p className="text-[10px] text-[#6B7280]">{t("howWasExperience")}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F5F5F5] flex items-center justify-center text-[#6B7280] hover:bg-[#E5E7EB]">&times;</button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center py-10">
            <svg className="animate-spin mb-3" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7BAE00" strokeWidth="2.5"><circle cx="12" cy="12" r="10" strokeOpacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" /></svg>
            <p className="text-[13px] font-medium text-[#7B8794]">{t("loadingRating")}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && <div className="text-[11px] text-[#DC3545] bg-red-50 rounded-lg px-3 py-2">{error}</div>}

            <div className="space-y-2.5">
              {HIRE_RATING_CRITERIA.map(item => (
                <div key={item.k} className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-[#1F2937]">{item.label}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s} type="button"
                        onClick={() => setCriterion(item.k, s)}
                        className="transition-transform active:scale-90">
                        <svg width="22" height="22" viewBox="0 0 24 24"
                          fill={criteria[item.k] >= s ? "#7BAE00" : "none"}
                          stroke={criteria[item.k] >= s ? "#7BAE00" : "#CBD5E1"} strokeWidth="1.5">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="form-label">{t("shortReview")}</label>
              <textarea className="input text-xs rounded-[10px]" rows={3} placeholder={t("tellUsExperience")}
                value={review} onChange={e => setReview(e.target.value)} maxLength={500} />
            </div>

            <button type="submit" disabled={submitting}
              className="w-full h-[40px] rounded-full bg-[#7BAE00] hover:bg-[#689300] text-white text-[11px] font-bold shadow-sm shadow-[#7BAE00]/10 transition-all flex items-center justify-center active:scale-[0.98]">
              {submitting ? "Submitting…" : (isUpdating ? t("updateRating") : t("submitRating"))}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
